import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Heart, Award, Users, BookOpen, ArrowRight, Quote, GraduationCap, Stethoscope, HandHeart, Lightbulb, Building } from 'lucide-react';
import { normalizeImageUrl } from '../lib/imageUrl';
import { usePage } from '../contexts/CmsContext';

const ABOUT_HERO = "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1920&q=80";
const CAMPUS_IMAGE = "https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80";

const values = [
  { icon: Heart, title: 'Compassion', description: 'Fostering empathy and patient-centered care in every aspect of medical practice.' },
  { icon: Award, title: 'Excellence', description: 'Striving for the highest standards in medical education, research, and clinical practice.' },
  { icon: Users, title: 'Integrity', description: 'Upholding ethical principles and professional conduct in all endeavors.' },
  { icon: BookOpen, title: 'Innovation', description: 'Embracing modern teaching methodologies and advancing medical knowledge.' },
];

const FALLBACK_CHAIRMAN = {
  name: 'Shri B. S. Khanna',
  role: 'Chairman (Gyanjeet Sewa Mission Trust)',
  desc: 'Strategic oversight and institutional governance',
};

const FALLBACK_LEADERSHIP = [
  { name: 'Medical Director Dr. D. P. Lokwani', role: 'Medical Director', desc: 'Clinical operations and medical service excellence' },
  { name: 'Executive Director Kaustubh Verma', role: 'Executive Director', desc: 'Administrative leadership and operational management' },
  { name: 'Dr. Nipun Agrawal', role: 'Senior Medical Faculty', desc: 'Medical education and clinical expertise' },
];

const About = () => {
  const { data: aboutCms } = usePage('about');
  const overviewImage = normalizeImageUrl(aboutCms?.overview?.image) || CAMPUS_IMAGE;
  const chairman = aboutCms?.chairman?.name ? aboutCms.chairman : FALLBACK_CHAIRMAN;
  const leadership = aboutCms?.leadership_team?.length ? aboutCms.leadership_team : FALLBACK_LEADERSHIP;
  return (
    <div className="page-transition" data-testid="about-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${ABOUT_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="about-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              About SSMCH
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Shaping the Future of Healthcare
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Sukh Sagar Medical College & Hospital stands as a beacon of medical excellence, committed to producing skilled and compassionate healthcare professionals.
            </p>
          </div>
        </div>
      </section>

      {/* College Overview */}
      <section className="section-padding bg-white" data-testid="overview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Established {aboutCms?.establishment_year || '2010'}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                College Overview
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {(aboutCms?.overview?.paragraphs?.length ? aboutCms.overview.paragraphs : [
                  'Sukh Sagar Medical College & Hospital (SSMCH), Jabalpur, was established in 2010 under the aegis of the Gyanjeet Sewa Mission Trust with a vision to provide exemplary medical education and compassionate healthcare. Located in Madhya Pradesh, it is an NMC-recognized institution dedicated to addressing the healthcare needs of central India.',
                  'Our institution combines rigorous academic programs with hands-on clinical training at our attached 930-bed teaching hospital, ensuring that students receive comprehensive medical education that prepares them for the challenges of modern healthcare.',
                  'Since our establishment, SSMCH has graduated thousands of skilled medical professionals who are making a difference in healthcare worldwide. Our hospital serves as a tertiary care center, providing specialized medical services while serving as a practical learning environment for our students.'
                ]).map((para, i) => (<p key={i}>{para}</p>))}
              </div>
            </div>
            <div>
              <img 
                src={overviewImage} 
                alt="SSMCH Campus" 
                className="rounded-2xl shadow-xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gyanjeet Sewa Mission Trust */}
      <section className="section-padding bg-[#1A202C]" data-testid="trust-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className="inline-block bg-[#D4A84B]/20 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Founding Organization
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Gyanjeet Sewa Mission Trust
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              The Gyanjeet Sewa Mission Trust is the founding organization behind Sukh Sagar Medical College & Hospital. Established with a noble mission to serve society through education and healthcare, the trust has been instrumental in creating institutions that make a lasting impact on communities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 bg-white/5 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#4ECDC4]/20 rounded-xl flex items-center justify-center mb-4">
                  <Target className="text-[#4ECDC4]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-4">Trust Objectives</h3>
                <ul className="space-y-3 text-gray-300 text-sm">
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#4ECDC4] rounded-full mt-1.5 shrink-0" />Promote quality medical education</li>
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#4ECDC4] rounded-full mt-1.5 shrink-0" />Provide accessible healthcare services</li>
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#4ECDC4] rounded-full mt-1.5 shrink-0" />Support community welfare initiatives</li>
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#4ECDC4] rounded-full mt-1.5 shrink-0" />Foster research and innovation</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-0 bg-white/5 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#D4A84B]/20 rounded-xl flex items-center justify-center mb-4">
                  <HandHeart className="text-[#D4A84B]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-4">Social Impact</h3>
                <ul className="space-y-3 text-gray-300 text-sm">
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#D4A84B] rounded-full mt-1.5 shrink-0" />Educational scholarships for deserving students</li>
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#D4A84B] rounded-full mt-1.5 shrink-0" />Free medical camps in rural areas</li>
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#D4A84B] rounded-full mt-1.5 shrink-0" />Healthcare awareness programs</li>
                  <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#D4A84B] rounded-full mt-1.5 shrink-0" />Emergency medical assistance</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="vision-mission-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-[#4ECDC4] to-[#3DBDB4]" />
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#4ECDC4]/10 rounded-2xl flex items-center justify-center mb-6">
                  <Eye className="text-[#4ECDC4]" size={32} />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#1A202C] mb-4">
                  Our Vision
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  To be a center of excellence in medical education that produces ethical, competent, and compassionate healthcare professionals who contribute meaningfully to society and advance the practice of medicine through innovation, research, and dedicated patient care.
                </p>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-[#D4A84B] to-[#F4D078]" />
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#D4A84B]/10 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="text-[#D4A84B]" size={32} />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#1A202C] mb-4">
                  Our Mission
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#D4A84B] rounded-full mt-2 flex-shrink-0" />
                    <span>Provide quality medical education aligned with national standards</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#D4A84B] rounded-full mt-2 flex-shrink-0" />
                    <span>Foster clinical competence through early patient exposure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#D4A84B] rounded-full mt-2 flex-shrink-0" />
                    <span>Promote research and innovation in medical sciences</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#D4A84B] rounded-full mt-2 flex-shrink-0" />
                    <span>Serve the healthcare needs of the community</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-white" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#9ACD32]/10 text-[#9ACD32] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Guiding Principles
            </h2>
            <p className="text-gray-600 text-lg">
              The core values that shape our approach to medical education and patient care.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="card-hover group border-0 shadow-sm text-center"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#4ECDC4]/20 to-[#4ECDC4]/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="text-[#4ECDC4]" size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Chairman's Message */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="chairman-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-8">
                  <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                    From the Chairman's Desk
                  </span>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C]">
                    A Message from Our Leadership
                  </h2>
                </div>
                <div className="relative">
                  <Quote className="absolute -top-2 -left-2 text-[#D4A84B]/20" size={48} />
                  <div className="prose prose-lg max-w-none text-gray-600 pl-8">
                    <p className="leading-relaxed mb-4 italic">
                      "At Sukh Sagar Medical College & Hospital, we believe that the foundation of exceptional healthcare lies in exceptional education. Our commitment extends beyond creating skilled medical professionals; we strive to nurture compassionate healers who understand that medicine is both a science and an art of caring."
                    </p>
                    <p className="leading-relaxed mb-4 italic">
                      "Through our state-of-the-art facilities, experienced faculty, and comprehensive curriculum, we prepare our students to meet the evolving challenges of modern healthcare. Our hospital serves not only as a center of healing but also as a living laboratory where theory meets practice, ensuring our graduates are ready to make meaningful contributions to society."
                    </p>
                    <p className="leading-relaxed italic">
                      "We remain committed to our founding principles of excellence, integrity, and service, as we continue to advance medical education and healthcare delivery for the benefit of our community and beyond."
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Top Leadership */}
      <section className="section-padding bg-white" data-testid="leadership-team-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Governance
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Top Leadership
            </h2>
            <p className="text-gray-600 text-lg">
              Meet our distinguished leadership team driving excellence in medical education and healthcare.
            </p>
          </div>
          {/* Chairman highlight (single, centered) */}
          <div className="max-w-md mx-auto mb-8">
            <Card className="card-hover border-0 shadow-md group bg-gradient-to-br from-[#1A202C] to-[#2D3748] text-white" data-testid="chairman-card">
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 ring-4 ring-[#4ECDC4]/30 group-hover:scale-110 transition-transform overflow-hidden bg-white/10 flex items-center justify-center">
                  {chairman.photo ? (
                    <img src={normalizeImageUrl(chairman.photo)} alt={chairman.name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="text-[#4ECDC4]" size={32} />
                  )}
                </div>
                <h3 className="font-heading font-bold mb-1 text-lg">{chairman.name}</h3>
                <p className="text-[#D4A84B] text-sm font-semibold mb-3">{chairman.role}</p>
                <p className="text-white/70 text-sm">{chairman.desc}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {leadership.map((person, index) => (
              <Card key={index} className="card-hover border-0 shadow-sm group" data-testid={`leader-card-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 rounded-full mx-auto mb-4 group-hover:scale-110 transition-transform overflow-hidden bg-gradient-to-br from-[#1A202C] to-[#2D3748] flex items-center justify-center ring-2 ring-[#4ECDC4]/20">
                    {person.photo ? (
                      <img src={normalizeImageUrl(person.photo)} alt={person.name} className="w-full h-full object-cover" />
                    ) : (
                      <Users className="text-[#4ECDC4]" size={26} />
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-[#1A202C] mb-1 text-sm">{person.name}</h3>
                  <p className="text-[#D4A84B] text-xs font-semibold mb-2">{person.role}</p>
                  <p className="text-gray-500 text-xs">{person.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Message */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="leadership-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-8">
                  <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                    Leadership Message
                  </span>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C]">
                    From the Dean's Desk
                  </h2>
                </div>
                
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    <div className="w-44 h-56 rounded-xl overflow-hidden shadow-lg border-4 border-[#D4A84B]/20">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/v0kgpsrn_Dean%20Dr%20Kasar%202.jpg" 
                        alt="Dr. Pradeep Kumar Kasar - Dean, SSMCH" 
                        className="w-full h-full object-cover object-[25%_top]"
                        data-testid="dean-photo"
                      />
                    </div>
                    <div className="text-center mt-4">
                      <p className="font-heading font-bold text-[#1A202C]">Dr. Pradeep Kumar Kasar</p>
                      <p className="text-xs text-gray-500">MBBS, MD (Community Medicine)</p>
                      <p className="text-sm text-[#D4A84B] font-medium">Dean, SSMCH</p>
                    </div>
                  </div>

                  <div className="prose prose-lg max-w-none text-gray-600">
                    <p className="leading-relaxed mb-4">
                      Welcome to Sukh Sagar Medical College & Hospital. Our institution is committed to providing quality medical education that prepares students for the challenges and responsibilities of the medical profession.
                    </p>
                    <p className="leading-relaxed mb-4">
                      Dr. Kasar is a Community Physician expert in Preventing Diseases, Epidemiology, Hospital Administration and Management, with wide knowledge in Research Methodology and Biostatistics. He has offered expert medical advice and guidance to countless patients across Madhya Pradesh.
                    </p>
                    <p className="leading-relaxed mb-4">
                      We believe in nurturing not just medical knowledge, but also the values of compassion, integrity, and service that define true healthcare professionals. Our curriculum integrates theoretical learning with clinical practice, ensuring that our graduates are well-equipped to serve patients and communities.
                    </p>
                    <p className="leading-relaxed">
                      I invite aspiring medical students to join our institution and embark on a rewarding journey towards becoming healthcare professionals who make a meaningful difference in people's lives.
                    </p>
                    <div className="mt-6 pt-4 border-t border-gray-200 not-prose">
                      <p className="text-xs text-gray-400">Former Dean, NSCB Medical College &middot; Former Medical Superintendent, NSCB Medical College and Cancer Hospital</p>
                      <p className="text-xs text-gray-400 mt-1">At SSMCH since 1st July 2022</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A202C]" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Begin Your Medical Journey at SSMCH
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Join a community of aspiring healthcare professionals and take the first step towards a rewarding career in medicine.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/admissions">
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
                data-testid="about-apply-btn"
              >
                Apply Now
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1A202C] rounded-full px-8 h-12 font-medium transition-all"
                data-testid="about-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
